import { assertCanHandle } from '@lucidlink/tscore';
import { Logging } from '#common/logging';
import { withStats } from '#common/stats';
import { SnapshotError } from '#common/types';
import { TelemetryName } from '#telemetry/types';
import { SnapshotProxy } from './proxy/snapshot-proxy';
import { SnapshotManagerStats } from './types';
export function createSnapshotManager(fsId, client, kvHelperWasm, utilsWasm, session, authorizer) {
    const proxy = new SnapshotProxy(fsId, client, utilsWasm, session);
    return new SnapshotManagerImpl(proxy, authorizer, kvHelperWasm);
}
class SnapshotManagerImpl {
    proxy;
    authorizer;
    kvHelperWasm;
    log;
    statistics;
    constructor(proxy, authorizer, kvHelperWasm) {
        this.proxy = proxy;
        this.authorizer = authorizer;
        this.kvHelperWasm = kvHelperWasm;
        this.log = Logging.getLogger('SnapshotManager');
        this.statistics = new SnapshotManagerStats(this.log);
    }
    async create(label) {
        return withStats(this.statistics.create, () => {
            const operation = TelemetryName.CREATE_SNAPSHOT;
            const telemetryValue = this.kvHelperWasm.makeTelemetryCreateSnapshotTuple(label);
            return this.authorizer.executeWithAdminPrivileges(() => {
                return this.doCreate(label);
            }, { operation, telemetryValue });
        }, { label });
    }
    delete(snapshotId) {
        return withStats(this.statistics.delete, () => {
            const operation = TelemetryName.DELETE_SNAPSHOT;
            const telemetryValue = this.kvHelperWasm.makeTelemetryDeleteSnapshotTuple(snapshotId);
            return this.authorizer.executeWithAdminPrivileges(() => {
                return this.doDelete(snapshotId);
            }, { operation, telemetryValue });
        }, { snapshotId });
    }
    list() {
        return withStats(this.statistics.list, async () => {
            try {
                return await this.proxy.list();
            }
            catch (err) {
                assertCanHandle(err, (e) => e instanceof Error);
                const msg = `Failed to list snapshots: ${err.message}`;
                this.log.error({}, msg);
                throw new SnapshotError(msg);
            }
        });
    }
    stats() {
        return this.statistics;
    }
    async doCreate(label) {
        try {
            return await this.proxy.create(label);
        }
        catch (err) {
            assertCanHandle(err, (e) => e instanceof Error);
            const msg = `Failed to create snapshot with label ${label}: ${err.message}`;
            this.log.error({ label }, msg);
            throw new SnapshotError(msg);
        }
    }
    async doDelete(id) {
        try {
            return await this.proxy.delete(id);
        }
        catch (err) {
            assertCanHandle(err, (e) => e instanceof Error);
            const msg = `Failed to delete snapshot with ID ${id}: ${err.message}`;
            this.log.error({ id }, msg);
            throw new SnapshotError(msg);
        }
    }
}
